import axios from "axios";

import { buildCxApiUrl, CX_API_ENDPOINTS } from "../config/cxApiConfig";

const STRIPE_SCRIPT_ID = "cx-stripe-js";
const STRIPE_SCRIPT_URL = "https://js.stripe.com/clover/stripe.js";

export interface CXPublicStripeTipConfig {
  connectedAccountId: string;
  publishableKey: string;
  technicianName: string;
}

export interface CXPublicStripeTipCheckoutInput {
  componentId?: string;
  coverProcessingFee: boolean;
  idempotencyKey: string;
  paymentOption: "card" | "cash_app";
  selectedAmountCents: number;
}

export interface CXPublicStripeTipCheckoutResponse {
  clientSecret: string;
  sessionId: string;
}

export interface CXPublicStripeTipStatus {
  componentId?: string;
  selectedAmountCents?: number;
  totalAmountCents?: number;
  processingFeeCents?: number;
  technicianTipCents?: number;
  paymentOption?: "card" | "cash_app";
  paymentStatus: string;
  sessionId: string;
  status: string;
  transactionStatus: string;
}

export interface StripeEmbeddedCheckout {
  destroy: () => void;
  mount: (selector: string | HTMLElement) => void;
}

interface StripeClient {
  initEmbeddedCheckout: (options: {
    clientSecret: string;
    onComplete: () => void;
  }) => Promise<StripeEmbeddedCheckout>;
}

type StripeFactory = (
  publishableKey: string,
  options: { stripeAccount: string },
) => StripeClient;

function stripeFactory(): StripeFactory | undefined {
  return (window as typeof window & { Stripe?: StripeFactory }).Stripe;
}

export async function loadStripeJS(): Promise<StripeFactory> {
  const existingFactory = stripeFactory();
  if (existingFactory) return existingFactory;

  const existingScript = document.getElementById(
    STRIPE_SCRIPT_ID,
  ) as HTMLScriptElement | null;
  const script = existingScript ?? document.createElement("script");
  if (!existingScript) {
    script.id = STRIPE_SCRIPT_ID;
    script.src = STRIPE_SCRIPT_URL;
    script.async = true;
    document.head.appendChild(script);
  }

  await new Promise<void>((resolve, reject) => {
    if (stripeFactory()) {
      resolve();
      return;
    }
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Stripe checkout could not be loaded.")),
      { once: true },
    );
  });

  const loadedFactory = stripeFactory();
  if (!loadedFactory) throw new Error("Stripe checkout could not be loaded.");
  return loadedFactory;
}

export async function getPublicStripeTipConfig(
  surveyCode: string,
): Promise<CXPublicStripeTipConfig> {
  const response = await axios.get<CXPublicStripeTipConfig>(
    buildCxApiUrl(CX_API_ENDPOINTS.PUBLIC_SURVEY_STRIPE_TIP_CONFIG(surveyCode)),
  );
  return response.data;
}

export async function createPublicStripeTipCheckout(
  surveyCode: string,
  input: CXPublicStripeTipCheckoutInput,
): Promise<CXPublicStripeTipCheckoutResponse> {
  const response = await axios.post<CXPublicStripeTipCheckoutResponse>(
    buildCxApiUrl(CX_API_ENDPOINTS.PUBLIC_SURVEY_STRIPE_TIP_CHECKOUT(surveyCode)),
    input,
  );
  return response.data;
}

export async function confirmPublicStripeTipCheckout(
  surveyCode: string,
  sessionId: string,
): Promise<CXPublicStripeTipStatus> {
  const response = await axios.post<CXPublicStripeTipStatus>(
    buildCxApiUrl(
      CX_API_ENDPOINTS.PUBLIC_SURVEY_STRIPE_TIP_CONFIRM(surveyCode, sessionId),
    ),
  );
  return response.data;
}
